import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { faFile, faPlus, faUser } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleXmark, faFilePdf } from "@performyard/fontawesome/pro-solid-svg-icons";
import {
	ButtonIcon,
	ButtonStandard,
	InputNumeric,
	InputText,
	InputWrapper,
	Spinner,
	Text,
	toast,
	useWizard,
} from "@performyard/py-components";

import { WizardStepHeader } from "../../../../components/WizardStep";
import { InlineError } from "../components/InlineError";

import { signatureRequestTemplatesKeys } from "../../../../api/hooks/defs/signature-request-templates";
import { useWorkflowTemplate } from "../../../../api/hooks/defs/workflow-templates";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import usePageWidthBreakpoint from "../../../../hooks/usePageWidthBreakpoint";

import { getSignatureRequestTemplateDetail } from "../../../../api/services/defs/signature-request-templates";

import { getCompanySettingsContext } from "../../../../selectors/settings";

import { AssigneeSelect } from "../AssigneeSelect";
import { SignatureTemplateDetailsModal } from "../SignatureTemplateDetailsModal";
import { SignatureTemplatePickerModal, type SignatureTemplateSelection } from "../SignatureTemplatePickerModal";
import {
	DEACTIVATED_ERROR,
	type LaunchTodoRow,
	NAME_MAX_LENGTH,
	SIGNER_WILDCARDS,
	TODOS_MAX_COUNT,
	TODOS_STEP_TITLE,
	type TodoRowErrors,
	buildEmptyTodoRow,
	toLaunchTodoRows,
	toSignerValue,
	validateTodoRow,
} from "../launch-form";

type DocModalState = {
	rowId: string;
	mode: "pick" | "view";
};

const NAME_COLUMN = "min-w-0 grow basis-[180px]";
const ASSIGNEE_COLUMN = "w-[240px] min-w-[140px] shrink";
const DAYS_COLUMN = "w-[110px] min-w-[88px] shrink";
const SIGNER_COLUMN = "w-[240px]";
const SIGNER_BLOCK_OFFSET = "pr-[150px]";
// Below this the four columns can no longer sit on one line without the inputs
// collapsing, so each row stacks its fields instead.
const STACK_BREAKPOINT = 860;

/**
 * The wide layout's column labels. They live above the list rather than inside the first
 * row so a label that wraps ("Days to Complete" does at this column width) grows the
 * header instead of pushing that one column's input below its neighbours'.
 */
const TodoColumnHeaders = ({ docSigningEnabled }: { docSigningEnabled: boolean }) => {
	const label = (text: string) => (
		<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
			{text}
		</Text>
	);

	return (
		// `items-end` keeps the labels sitting on the header's baseline, so a wrapped label
		// grows upward and every column's input still starts at the same height.
		<div className="flex w-full items-end gap-2" data-testid="todo-column-headers">
			{docSigningEnabled && <div className="w-6 shrink-0" />}
			<div className={NAME_COLUMN}>{label("To-Do")}</div>
			<div className={ASSIGNEE_COLUMN}>{label("Assignee")}</div>
			<div className={DAYS_COLUMN}>{label("Days to Complete")}</div>
			<div className="w-6 shrink-0" />
		</div>
	);
};

const TodoRow = ({
	row,
	index,
	isStacked,
	errors,
	showErrors,
	docSigningEnabled,
	onChange,
	onOpenDocModal,
	onRemove,
}: {
	row: LaunchTodoRow;
	index: number;
	isStacked: boolean;
	errors: TodoRowErrors;
	showErrors: boolean;
	docSigningEnabled: boolean;
	onChange: (row: LaunchTodoRow) => void;
	onOpenDocModal: (mode: DocModalState["mode"]) => void;
	onRemove: () => void;
}) => {
	const hasSignature = !!row.signatureTemplate;
	const deactivatedMessage = errors.message;
	const iconCellClassName = "flex h-8 w-6 shrink-0 items-center";
	// Wide rows are labelled once by `TodoColumnHeaders`; stacked rows have no header to
	// sit under, so each field carries its own label. Either way the inputs keep their
	// explicit `aria-label` — InputWrapper suppresses the placeholder fallback whenever it
	// supplies a context id, so an unlabelled row would otherwise announce as nameless.
	const showFieldLabels = isStacked;
	const rowLabel = `to-do ${index + 1}`;

	/* Gated like the legacy page: hidden when company doc-signing is off, but an
	   already-attached signature still shows in the assignee column below. */
	const documentButton = docSigningEnabled ? (
		<ButtonIcon
			icon={hasSignature ? faFilePdf : faFile}
			size={24}
			color={hasSignature ? "primary" : "neutral"}
			aria-label={
				hasSignature ? `View signature request for ${rowLabel}` : `Attach signature document to ${rowLabel}`
			}
			data-testid={`todo-document-button-${index}`}
			onClick={() => onOpenDocModal(hasSignature ? "view" : "pick")}
		/>
	) : null;

	const removeButton = (
		<ButtonIcon
			icon={faCircleXmark}
			size={24}
			color="neutral"
			aria-label={`Remove ${rowLabel}`}
			data-testid={`todo-remove-${index}`}
			onClick={onRemove}
		/>
	);

	const nameField = (
		<InputWrapper
			id={`todo-name-${index}`}
			label={showFieldLabels ? "To-Do" : undefined}
			error={showErrors ? errors.name : undefined}
		>
			<InputText
				value={row.name}
				onChange={(value) => onChange({ ...row, name: value.slice(0, NAME_MAX_LENGTH) })}
				placeholder="To-do name"
				errored={showErrors && !!errors.name}
				aria-label={`To-Do ${index + 1}`}
				data-testid={`todo-name-${index}`}
			/>
		</InputWrapper>
	);

	const assigneeField = hasSignature ? (
		<InputWrapper id={`todo-signature-assignee-${index}`} label={showFieldLabels ? "Assignee" : undefined}>
			<InputText
				value={`${row.signers.length} Signer${row.signers.length === 1 ? "" : "s"}`}
				onChange={() => undefined}
				disabled
				leftIcon={faUser}
				placeholder="Signature Template"
				aria-label={`Assignee ${index + 1}`}
				data-testid={`todo-signature-chip-${index}`}
			/>
		</InputWrapper>
	) : (
		<InputWrapper
			id={`todo-assignee-${index}`}
			label={showFieldLabels ? "Assignee" : undefined}
			error={showErrors || errors.assignee === DEACTIVATED_ERROR ? errors.assignee : undefined}
		>
			<AssigneeSelect
				value={row.assignee}
				onChange={(assignee) => onChange({ ...row, assignee })}
				placeholder="Make a selection"
				errored={(showErrors && !!errors.assignee) || (!showErrors && errors.assignee === DEACTIVATED_ERROR)}
				ariaLabel={`Assignee ${index + 1}`}
				id={`todo-assignee-${index}`}
				data-testid={`todo-assignee-${index}`}
			/>
		</InputWrapper>
	);

	const daysField = (
		<InputWrapper
			id={`todo-days-${index}`}
			label={showFieldLabels ? "Days to Complete" : undefined}
			error={showErrors ? errors.daysToComplete : undefined}
		>
			<InputNumeric
				allowEmpty
				precision={0}
				value={row.daysToComplete}
				onChange={(value) => onChange({ ...row, daysToComplete: value })}
				placeholder="e.g. 5"
				errored={showErrors && !!errors.daysToComplete}
				data-testid={`todo-days-${index}`}
			/>
		</InputWrapper>
	);

	const signersBlock = hasSignature && row.signers.length > 0 && (
		<div
			className={`flex flex-col gap-2 ${isStacked ? "w-full items-stretch" : "items-end"}`}
			data-testid={`todo-signers-${index}`}
		>
			<div className={isStacked ? "w-full" : "w-[390px] max-w-full"}>
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					{row.signatureTemplate?.name || "Signature Template"}
				</Text>
			</div>
			<div className={`flex flex-col gap-2.5 ${isStacked ? "w-full" : SIGNER_BLOCK_OFFSET}`}>
				{row.signers.map((signer, signerIndex) => {
					const signerError = errors.signers[signerIndex];
					const showSignerError = showErrors || signerError === DEACTIVATED_ERROR;
					return (
						// Signature steps are ordered and have no ids of their own.
						<InputWrapper
							key={signerIndex}
							id={`todo-signer-${index}-${signerIndex}`}
							label={`Signer ${signerIndex + 1}`}
							labelPosition={isStacked ? "top" : "left"}
							error={showSignerError ? signerError : undefined}
						>
							<div className={isStacked ? "w-full" : SIGNER_COLUMN}>
								<AssigneeSelect
									value={signer}
									onChange={(value) =>
										onChange({
											...row,
											signers: row.signers.map((current, i) =>
												i === signerIndex ? value : current
											),
										})
									}
									wildcards={SIGNER_WILDCARDS}
									errored={showSignerError && !!signerError}
									placeholder="Make a selection"
									ariaLabel={`Signer ${signerIndex + 1} for ${rowLabel}`}
									id={`todo-signer-${index}-${signerIndex}`}
									data-testid={`todo-signer-${index}-${signerIndex}`}
								/>
							</div>
						</InputWrapper>
					);
				})}
			</div>
		</div>
	);

	const inlineError = deactivatedMessage && (
		<div className="w-full">
			<InlineError message={deactivatedMessage} data-testid={`todo-deactivated-${index}`} />
		</div>
	);

	if (isStacked) {
		// Narrow widths: fields go full width and each carries its own label, with the
		// icon buttons lifted out of the field line onto their own bar.
		return (
			<div className="flex w-full flex-col gap-2" data-testid="todo-row">
				<div className={`flex items-center gap-2 ${documentButton ? "justify-between" : "justify-end"}`}>
					{documentButton}
					{removeButton}
				</div>
				{nameField}
				{assigneeField}
				<div className="w-[140px] max-w-full">{daysField}</div>
				{inlineError}
				{signersBlock}
			</div>
		);
	}

	return (
		<div className="flex w-full flex-col items-end gap-2">
			<div className="flex w-full items-start gap-2" data-testid="todo-row">
				{documentButton && <div className={iconCellClassName}>{documentButton}</div>}

				<div className={NAME_COLUMN}>{nameField}</div>

				<div className={ASSIGNEE_COLUMN}>{assigneeField}</div>

				<div className={DAYS_COLUMN}>{daysField}</div>

				<div className={iconCellClassName}>{removeButton}</div>
			</div>

			{inlineError}

			{signersBlock}
		</div>
	);
};

type TodosStepProps = {
	stepTitle: string;
	templateId: string | null;
	rows: LaunchTodoRow[];
	/** Accepts an updater so async row writes can't clobber concurrent edits. */
	onRowsChange: React.Dispatch<React.SetStateAction<LaunchTodoRow[]>>;
	loadedTemplateId: string | null;
	onTemplateLoaded: (templateId: string) => void;
	showErrors: boolean;
};

export const TodosStep = ({
	stepTitle,
	templateId,
	rows,
	onRowsChange,
	loadedTemplateId,
	onTemplateLoaded,
	showErrors,
}: TodosStepProps) => {
	const { setNextDisabled } = useWizard();
	const queryClient = useQueryClient();
	const { isDesktop } = usePageWidthBreakpoint(450, STACK_BREAKPOINT);
	const isStacked = !isDesktop;
	const [docModal, setDocModal] = useState<DocModalState | null>(null);
	const [isAttaching, setIsAttaching] = useState(false);

	const needsSeed = !!templateId && loadedTemplateId !== templateId;
	const { data: templateData } = useWorkflowTemplate(needsSeed ? (templateId ?? "") : "");

	useEffect(() => {
		if (needsSeed && templateId && templateData) {
			onRowsChange(
				toLaunchTodoRows(
					templateData.template.todos,
					templateData.display_user_map,
					templateData.signature_request_map
				)
			);
			onTemplateLoaded(templateId);
		}
	}, [needsSeed, templateId, templateData, onRowsChange, onTemplateLoaded]);

	useEffect(() => {
		setNextDisabled(needsSeed || isAttaching);
		return () => setNextDisabled(false);
	}, [needsSeed, isAttaching, setNextDisabled]);

	// Default `true` matches the legacy page this replaces. Note the rest of the app
	// (features.ts, AdminVerticalNavBar, Administration/SignatureRequests) defaults this
	// flag to `false`; reconciling needs confirmation that it exists in every Logan env.
	const { value: docSigningFeatureVisible } = useFeatureFlagQuery<boolean>("ff-is-document-signing-enabled", true);
	const companySettings = useSelector(getCompanySettingsContext);
	const docSigningEnabled =
		docSigningFeatureVisible && companySettings.getIn(["documentSigning", "state"], "disabled") === "enabled";

	const rowErrors = rows.map(validateTodoRow);

	const updateRow = (rowId: string, apply: (row: LaunchTodoRow) => LaunchTodoRow): void => {
		onRowsChange((current) => current.map((row) => (row._id === rowId ? apply(row) : row)));
	};

	const handleAttach = async (rowId: string, selection: SignatureTemplateSelection): Promise<void> => {
		setDocModal(null);
		setIsAttaching(true);
		try {
			const detail = await queryClient.fetchQuery({
				queryKey: signatureRequestTemplatesKeys.detail(selection.id),
				queryFn: () => getSignatureRequestTemplateDetail(selection.id),
			});
			// Applied through the updater, not the `rows` captured before the await, so
			// edits made to other rows during the fetch survive.
			updateRow(rowId, (row) => ({
				...row,
				assignee: null,
				signatureTemplate: { id: selection.id, name: detail.name || selection.name },
				signers: detail.signers.map((step) => toSignerValue(step.signer)),
			}));
		} catch (error) {
			Sentry.captureException(error);
			console.error("Error attaching signature template:", error);
			toast.error("There was an error getting the signature request template.");
		} finally {
			setIsAttaching(false);
		}
	};

	const docModalRow = docModal ? (rows.find((row) => row._id === docModal.rowId) ?? null) : null;

	if (needsSeed) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="flex flex-col items-center">
			<WizardStepHeader title={stepTitle} subtitle={TODOS_STEP_TITLE} />

			{/* `box-border` is load-bearing: the app loads Tailwind without preflight, so a
			    content-box `w-full` plus horizontal padding renders wider than the step's scroll
			    area and puts the padding outside it, leaving the rows flush against the edges.
			    `pr-6` matches the `gap-6` the wizard puts between the stepper and this pane, so
			    the rows sit the same distance from the pane's right edge as from its left. */}
			<div className="mt-8 box-border flex w-full max-w-[1096px] flex-col gap-4 pr-6 pb-8">
				{rows.length > 0 ? (
					<div className="flex flex-col gap-2">
						{!isStacked && <TodoColumnHeaders docSigningEnabled={docSigningEnabled} />}

						<ul
							// Reset the legacy global list styling the app applies to bare `ul`s.
							// Stacked rows separate on spacing alone, so they need more of it than
							// the gap between the fields inside a row.
							className={`m-0 flex list-none flex-col p-0 ${isStacked ? "gap-6" : "gap-2"}`}
							aria-label="Workflow to-dos"
							data-testid="todo-rows"
						>
							{rows.map((row, index) => (
								<li key={row._id}>
									<TodoRow
										row={row}
										index={index}
										isStacked={isStacked}
										errors={rowErrors[index]}
										showErrors={showErrors}
										docSigningEnabled={docSigningEnabled}
										onChange={(updated) => updateRow(row._id, () => updated)}
										onOpenDocModal={(mode) => setDocModal({ rowId: row._id, mode })}
										onRemove={() =>
											onRowsChange((current) =>
												current.filter((candidate) => candidate._id !== row._id)
											)
										}
									/>
								</li>
							))}
						</ul>
					</div>
				) : (
					<div className="rounded-1 border border-dashed border-neutral-3 px-4 py-6 text-center">
						<Text size="paragraph-3" weight="regular" color="neutral-6">
							No to-dos yet. Click &quot;Add To-Do&quot; to build out this workflow.
						</Text>
					</div>
				)}

				{showErrors && rows.length === 0 && (
					<InlineError message="Add at least one to-do to launch this workflow." data-testid="todos-error" />
				)}

				{rows.length >= TODOS_MAX_COUNT && (
					<Text size="caption" weight="semi-bold" color="red-6">
						You have reached the maximum number of to-dos
					</Text>
				)}

				<ButtonStandard
					type="button"
					variant="solid"
					color="primary"
					iconLeft={faPlus}
					disabled={rows.length >= TODOS_MAX_COUNT}
					data-testid="add-todo-button"
					onClick={() =>
						onRowsChange((current) =>
							current.length >= TODOS_MAX_COUNT ? current : [...current, buildEmptyTodoRow()]
						)
					}
				>
					Add To-Do
				</ButtonStandard>
			</div>

			{docModal?.mode === "pick" && docModalRow && (
				<SignatureTemplatePickerModal
					onClose={() => setDocModal(null)}
					onSelect={(selection) => handleAttach(docModalRow._id, selection)}
				/>
			)}

			{docModal?.mode === "view" && docModalRow?.signatureTemplate && (
				<SignatureTemplateDetailsModal
					templateId={docModalRow.signatureTemplate.id}
					templateName={docModalRow.signatureTemplate.name}
					signers={docModalRow.signers}
					onClose={() => setDocModal(null)}
					onRemove={() => {
						updateRow(docModalRow._id, (row) => ({
							...row,
							signatureTemplate: null,
							signers: [],
						}));
						setDocModal(null);
					}}
				/>
			)}
		</div>
	);
};
